// src/pages/BlogPostPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BlogService } from '../services/blog.service';
import type { BlogPost } from '../types/Blog';
import { useAuth } from '../context/AuthContext';

const formatDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: '2-digit' });
};

export default function BlogPostPage() {
  const { slugOrId } = useParams<{ slugOrId: string }>();
  const { isAuthenticated, user } = useAuth();

  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string>('');

  const backHref = useMemo(() => {
    if (isAuthenticated && user?.role === 'client') return '/blogs';
    return '/blog';
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    const id = (slugOrId || '').trim();
    if (!id) {
      setPost(null);
      setLoading(false);
      setErr('Publicación no encontrada.');
      return;
    }

    setLoading(true);
    setErr('');
    BlogService.getPublicPostBySlugOrId(id)
      .then(res => setPost(res || null))
      .catch(() => {
        setPost(null);
        setErr('No se pudo cargar la publicación.');
      })
      .finally(() => setLoading(false));
  }, [slugOrId]);

  if (loading) {
    return (
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '28px 16px' }}>
        <div>Cargando...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '28px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>Blog</h1>
          <Link to={backHref} style={{ fontWeight: 900, color: 'var(--color-primario)' }}>
            Volver
          </Link>
        </div>
        <div style={{ marginTop: 16, color: 'var(--color-texto-secundario)' }}>{err || 'Publicación no encontrada.'}</div>
      </div>
    );
  }

  const dateText = formatDate(post.publish_from || post.created_at);

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '28px 16px 40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {post.categories?.slice(0, 4).map(c => (
            <span
              key={String(c.id)}
              style={{
                fontSize: 12,
                fontWeight: 900,
                padding: '8px 12px',
                borderRadius: 999,
                background: 'var(--color-secundario)',
                color: 'var(--color-primario-oscuro)'
              }}
            >
              {c.name}
            </span>
          ))}
        </div>

        <Link to={backHref} style={{ fontWeight: 900, color: 'var(--color-primario)' }}>
          Volver
        </Link>
      </div>

      <div
        style={{
          marginTop: 14,
          background: 'var(--color-blanco)',
          border: '1px solid rgba(0,0,0,0.06)',
          borderRadius: 28,
          overflow: 'hidden',
          boxShadow: 'var(--sombra-elevacion-2)'
        }}
      >
        {post.cover_image_url ? (
          <img
            src={post.cover_image_url}
            alt={post.title}
            style={{ width: '100%', height: 320, objectFit: 'cover', display: 'block' }}
            onError={e => (e.currentTarget.style.display = 'none')}
          />
        ) : null}

        <div style={{ padding: 20 }}>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 950, lineHeight: 1.15 }}>{post.title}</h1>

          {dateText ? (
            <div style={{ marginTop: 10, color: 'var(--color-texto-secundario)', fontWeight: 700, fontSize: 13 }}>
              {dateText}
            </div>
          ) : null}

          {post.excerpt ? (
            <p style={{ margin: '14px 0 0', color: 'var(--color-texto-secundario)', lineHeight: 1.65, fontSize: 16 }}>
              {post.excerpt}
            </p>
          ) : null}

          <div
            style={{
              marginTop: 18,
              paddingTop: 18,
              borderTop: '1px solid rgba(0,0,0,0.06)',
              color: 'var(--color-texto-principal)',
              lineHeight: 1.75,
              fontSize: 16
            }}
            dangerouslySetInnerHTML={{ __html: post.content || '' }}
          />
        </div>
      </div>
    </div>
  );
}
